import { onBeforeUnmount, ref } from 'vue'
import type LessonPracticeApi from '../../api/v2/api/lesson_practice'

interface Options {
  lessonId: () => number
  threadId: () => string
  agentId?: () => number | string
  courseId?: () => number
  fingerprint?: (mode: 'focused' | 'comprehensive') => string
  api: LessonPracticeApi
  onGenerating: (value: boolean) => void
  onProgress?: (value: { progress: number; stage: string }) => void
  onReady: (value: { examId: number; userExamId?: number }) => void
  onStale?: () => Promise<void>
}
export function useLessonPractice(options: Options) {
  const practiceBusy = ref(false)
  const practiceWaiting = ref(false)
  const practiceError = ref('')
  const practiceFocus = ref('')
  let generation = 0
  let disposed = false
  const prefix = (lesson: number, thread: string, course: number) => `lesson-practice-job:${lesson}:${thread}:${course}:`
  const key = (lesson: number, thread: string, course: number, mode: string, fingerprint: string) => `${prefix(lesson, thread, course)}${mode}:${fingerprint}`
  onBeforeUnmount(() => { disposed = true; generation++ })
  async function poll(lesson: number, thread: string, course: number, storageKey: string, id: string, token: number) {
    const current = () => !disposed && generation === token && options.lessonId() === lesson && options.threadId() === thread && (options.courseId?.() || 0) === course
    for (let i = 0; i < 35 && current(); i++) {
      const status = await options.api.status(lesson, id, course)
      if (!current()) return
      if (status.status === 'failed') {
        localStorage.removeItem(storageKey)
        throw new Error(status.message || '問題を作成できませんでした。もう一度お試しください。')
      }
      if (status.status === 'ready' && status.examId) {
        localStorage.removeItem(storageKey)
        practiceFocus.value = status.focus || '会話をもとにした練習問題を左側に表示しました。'
        options.onReady({ examId: status.examId, userExamId: status.userExamId })
        return
      }
      options.onProgress?.({ progress: status.progress ?? 5, stage: status.stage || '作成を開始しています' })
      if (i >= 15) practiceWaiting.value = true
      await new Promise(resolve => setTimeout(resolve, Math.min(2000 * 2 ** Math.floor(i / 10), 15000)))
    }
    if (current()) throw new Error('生成に時間がかかっています。もう一度ボタンを押すと進行状況を確認できます。')
  }
  async function execute(resumeOnly: boolean, mode: 'focused' | 'comprehensive' = 'focused') {
    const lesson = options.lessonId(), thread = options.threadId(), course = options.courseId?.() || 0
    if (!lesson || !thread || disposed) return
    // Changing thread invalidates old polling and cannot render an exam into the new lesson.
    const token = ++generation
    const fingerprint = options.fingerprint?.(mode) || ''
    const storageKey = resumeOnly
      ? Object.keys(localStorage).find(item => item.startsWith(prefix(lesson, thread, course)))
      : key(lesson, thread, course, mode, fingerprint)
    // A deliberate click always starts a new exam. Only reload recovery uses the saved ticket.
    const cached = resumeOnly && storageKey ? localStorage.getItem(storageKey) : null
    if (!resumeOnly && storageKey) localStorage.removeItem(storageKey)
    practiceError.value = ''; practiceFocus.value = ''; practiceWaiting.value = false
    if (resumeOnly && !cached) { practiceBusy.value = false; options.onGenerating(false); return }
    practiceBusy.value = true; options.onProgress?.({ progress: 5, stage: '作成を開始しています' }); options.onGenerating(true)
    try {
      const requestId = window.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(36).slice(2)}`
      const id = cached || (await options.api.create(lesson, thread, mode, options.agentId?.(), course, fingerprint, requestId)).id
      if (!id) throw new Error('生成を開始できませんでした。')
      if (storageKey) localStorage.setItem(storageKey, id)
      await poll(lesson, thread, course, storageKey || '', id, token)
    } catch (e: any) {
      if (e?.baseResponse?.data?.data?.settings && options.onStale) await options.onStale()
      const serverMessage = e?.baseResponse?.data?.message
      if (!disposed && token === generation) practiceError.value = e?.baseResponse?.data?.data?.settings
        ? '設定が更新されました。表示を確認してもう一度実行してください。'
        : (typeof serverMessage === 'string' ? serverMessage : e.message || '問題を作成できませんでした。')
    } finally {
      if (!disposed && token === generation) { practiceBusy.value = false; practiceWaiting.value = false; options.onGenerating(false) }
    }
  }
  const createPractice = (mode: 'focused' | 'comprehensive' = 'focused') => { if (!practiceBusy.value) return execute(false, mode) }
  const resumePractice = () => { if (!practiceBusy.value) return execute(true) }
  return { practiceBusy, practiceWaiting, practiceError, practiceFocus, createPractice, resumePractice }
}
